import React, { useState } from 'react';
import { useAdmin } from '../../hooks/useAdmin';
import LoadingState from '../shared/LoadingState';
import { Edit, Trash2, Calendar, Clock, X, Filter, AlertCircle } from 'lucide-react';

export default function AllMeetingsTable() {
  const { meetings, users, meetingTypes, updateMeetingStatus, deleteMeeting, refresh, loading: dataLoading } = useAdmin();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    meeting_type_id: '',
    status: '',
    convener_id: '',
    start_date: '',
    end_date: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const filteredMeetings = meetings.filter(meeting => {
    const matchesType = !filters.meeting_type_id || meeting.meeting_type_id === filters.meeting_type_id;
    const matchesStatus = !filters.status || meeting.status === filters.status;
    const matchesConvener = !filters.convener_id || meeting.created_by === filters.convener_id;
    const matchesStartDate = !filters.start_date || meeting.date >= filters.start_date;
    const matchesEndDate = !filters.end_date || meeting.date <= filters.end_date;
    
    return matchesType && matchesStatus && matchesConvener && matchesStartDate && matchesEndDate;
  });

  const handleEditMeeting = async (meetingId: string, status: string) => {
    setLoading(true);
    setError('');

    const result = await updateMeetingStatus(meetingId, status);
    
    if (result.data) {
      setShowEditModal(false);
      refresh();
    } else {
      setError(result.error?.message || 'Failed to update meeting');
    }
    
    setLoading(false);
  };

  const handleDeleteMeeting = async (meetingId: string) => {
    setLoading(true);
    setError('');

    const result = await deleteMeeting(meetingId);
    
    if (result.error) {
      setError(result.error?.message || 'Failed to delete meeting');
    } else {
      setShowDeleteModal(false);
      refresh();
    }
    
    setLoading(false);
  };

  const openEditModal = (meeting: any) => {
    setEditingMeeting(meeting);
    setShowEditModal(true);
  };

  const openDeleteModal = (meeting: any) => {
    setEditingMeeting(meeting);
    setShowDeleteModal(true);
  };

  const closeModals = () => {
    setShowEditModal(false);
    setShowDeleteModal(false);
    setEditingMeeting(null);
    setError('');
  };

  const clearFilters = () => {
    setFilters({
      meeting_type_id: '',
      status: '',
      convener_id: '',
      start_date: '',
      end_date: ''
    });
  };

  const updateFilter = (key: string, value: string) => {
    setFilters((prev: any) => ({
      ...prev,
      [key]: value || undefined
    }));
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Filter size={18} className="text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              {showFilters ? 'Hide' : 'Show'} Filters
            </button>
            {Object.values(filters).some(v => v) && (
              <button
                onClick={clearFilters}
                className="px-3 py-1 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition"
              >
                Clear All
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Meeting Type
              </label>
              <select
                value={filters.meeting_type_id}
                onChange={(e) => updateFilter('meeting_type_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              >
                <option value="">All Types</option>
                {meetingTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name} - {type.level}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => updateFilter('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              >
                <option value="">All Status</option>
                <option value="Scheduled">Scheduled</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Convener
              </label>
              <select
                value={filters.convener_id}
                onChange={(e) => updateFilter('convener_id', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              >
                <option value="">All Conveners</option>
                {users.filter(u => !u.is_admin).map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name} - {user.phone}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Start Date
              </label>
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => updateFilter('start_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                End Date
              </label>
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => updateFilter('end_date', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>
          </div>
        )}

        {/* Results Count */}
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {dataLoading ? 'Loading meetings...' : `${filteredMeetings.length} meetings found`}
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Meetings Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {dataLoading ? (
          <LoadingState title="Loading Meetings..." minHeight="min-h-[300px]" />
        ) : filteredMeetings.length > 0 ? (
          <div>
            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Meeting Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Instance ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Convener
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredMeetings.map((meeting) => (
                    <tr key={meeting.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Calendar size={20} className="text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{meeting.date}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {meeting.meeting_types?.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {meeting.meeting_types?.level}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-xs text-gray-500">
                          {meeting.meeting_types?.name}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {meeting.time ? (
                          <div className="flex items-center text-sm text-gray-500">
                            <Clock size={14} className="mr-1" />
                            {meeting.time}
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No time</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {meeting.users?.name || 'Unknown'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {meeting.users?.phone || 'No phone'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          meeting.status === 'Completed' 
                            ? 'bg-green-100 text-green-800' 
                            : meeting.status === 'Scheduled'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {meeting.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => openEditModal(meeting)}
                            className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => openDeleteModal(meeting)}
                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="sm:hidden space-y-3 p-4">
              {filteredMeetings.map((meeting) => (
                <div key={meeting.id} className="border border-gray-200 rounded-lg p-4 bg-white hover:bg-gray-50 transition space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-900">{meeting.meeting_types?.name}</div>
                      <div className="text-xs text-gray-500">{meeting.meeting_types?.level}</div>
                    </div>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      meeting.status === 'Completed' 
                        ? 'bg-green-100 text-green-800' 
                        : meeting.status === 'Scheduled'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {meeting.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 bg-gray-50 p-2.5 rounded-md">
                    <div className="flex items-center">
                      <Calendar size={14} className="mr-1.5 text-blue-600 flex-shrink-0" />
                      <span>{meeting.date}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock size={14} className="mr-1.5 text-gray-500 flex-shrink-0" />
                      <span>{meeting.time || 'No time'}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-xs">
                    <div>
                      <div className="text-gray-900 font-medium">{meeting.users?.name || 'Unknown Convener'}</div>
                      <div className="text-gray-500">{meeting.users?.phone}</div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => openEditModal(meeting)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition"
                        title="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => openDeleteModal(meeting)}
                        className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {meetings.length === 0 ? (
              <div>
                <Calendar size={48} className="mx-auto mb-4 text-gray-300" />
                <p>No meetings found in the system.</p>
                <p className="text-sm mt-2">Meetings will appear here once conveners create them.</p>
              </div>
            ) : (
              <div>
                <Filter size={48} className="mx-auto mb-4 text-gray-300" />
                <p>No meetings match your filter criteria.</p>
                <p className="text-sm mt-2">Try adjusting your filters.</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Edit Meeting Modal */}
      {showEditModal && editingMeeting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Update Meeting Status</h2>
              <button
                onClick={closeModals}
                disabled={loading}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">{editingMeeting.meeting_types?.name}</h3>
                <p className="text-sm text-gray-500">Meeting: {editingMeeting.meeting_types?.name}</p>
                <p className="text-sm text-gray-500">Date: {editingMeeting.date}</p>
                {editingMeeting.time && (
                  <p className="text-sm text-gray-500">Time: {editingMeeting.time}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={editingMeeting.status}
                  onChange={(e) => setEditingMeeting({ ...editingMeeting, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  disabled={loading}
                >
                  <option value="Scheduled">Scheduled</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
            </div>

            <div className="flex space-x-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={closeModals}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleEditMeeting(editingMeeting.id, editingMeeting.status)}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update Status'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Meeting Modal */}
      {showDeleteModal && editingMeeting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Delete Meeting</h2>
              <button
                onClick={closeModals}
                disabled={loading}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle size={24} className="text-red-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Are you sure?</h3>
                  <p className="text-sm text-gray-500">
                    This action cannot be undone. This will permanently delete the meeting and all associated attendance records.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-gray-900">{editingMeeting.meeting_types?.name}</h4>
                <p className="text-sm text-gray-600">{editingMeeting.date} {editingMeeting.time}</p>
                <p className="text-xs text-gray-500 mt-1">ID: {editingMeeting.instance_id}</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  {error}
                </div>
              )}
            </div>

            <div className="flex space-x-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={closeModals}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteMeeting(editingMeeting.id)}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete Meeting'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
